import React, { Component, Fragment } from 'react';
import { Col, Row, OverlayTrigger, Tooltip, FormControl, FormGroup, Button } from 'react-bootstrap';
import { SketchPicker } from 'react-color';
import { translate } from 'react-i18next';
import './RichTextMenu.css';

class RichTextMenu extends Component {
    constructor(props) {
        super(props);

        this.state = {
            showColorPicker: false,
            showStrokeColorPicker: false,
            editingText: false,
            textValue: '',
            tempColor: null,
        };
        this.savedSelection = null;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.selectedLayerIndex !== this.props.selectedLayerIndex && this.props.selectedLayer) {
            this.setState({
                editingText: false,
                textValue: this.props.selectedLayer.text || '',
            });
        }
    }

    handleTextChange = (e) => {
        this.setState({ textValue: e.target.value });
    };

    handleTextBlur = () => {
        const { selectedLayerIndex, onUpdateLayer } = this.props;
        if (selectedLayerIndex !== null) {
            onUpdateLayer(selectedLayerIndex, { text: this.state.textValue });
        }
        this.setState({ editingText: false });
    };

    handleBold = () => {
        const { onApplyBold } = this.props;
        if (onApplyBold) {
            onApplyBold();
        }
    };

    handleItalic = () => {
        const { onApplyItalic } = this.props;
        if (onApplyItalic) {
            onApplyItalic();
        }
    };

    handleUnderline = () => {
        const { onApplyUnderline } = this.props;
        if (onApplyUnderline) {
            onApplyUnderline();
        }
    };

    handleAlignChange = (align) => {
        const { selectedLayerIndex, onUpdateLayer } = this.props;
        if (selectedLayerIndex !== null) {
            onUpdateLayer(selectedLayerIndex, { textAlign: align });
        }
    };

    handleFontChange = (e) => {
        const { selectedLayerIndex, onUpdateLayer } = this.props;
        if (selectedLayerIndex !== null) {
            onUpdateLayer(selectedLayerIndex, { fontFamily: e.target.value });
        }
    };

    handleFontSizeChange = (e) => {
        const { selectedLayerIndex, onUpdateLayer } = this.props;
        if (selectedLayerIndex !== null) {
            const value = e.target.value;
            // Allow empty string while typing
            if (value === '') {
                return;
            }
            const size = parseInt(value, 10);
            if (!isNaN(size) && size >= 8 && size <= 300) {
                onUpdateLayer(selectedLayerIndex, { fontSize: size });
            }
        }
    };

    handleColorPickerOpen = () => {
        // Save the current text selection using the canvas ref
        if (this.props.onSaveSelection) {
            this.savedSelection = this.props.onSaveSelection();
        }
        this.pendingColor = null;
        this.setState({
            showColorPicker: true,
            tempColor: (this.props.selectedLayer && this.props.selectedLayer.textColour) || '#000000'
        });
    };

    handleColorPickerClose = () => {
        // Apply the final pending color if any
        if (this.pendingColor && this.savedSelection) {
            const { selectedLayerIndex, onApplyColor } = this.props;
            if (selectedLayerIndex !== null && onApplyColor) {
                onApplyColor(this.pendingColor, this.savedSelection);
            }
        }
        this.setState({
            showColorPicker: false,
            tempColor: null
        });
        this.savedSelection = null;
        this.pendingColor = null;
    };

    handleColorChange = (color) => {
        // Update visual display and store the color, don't apply it yet (to avoid re-renders breaking the range)
        this.pendingColor = color.hex;
        this.setState({ tempColor: color.hex });
    };

    handleStrokeColorChange = (color) => {
        const { selectedLayerIndex, onUpdateLayer } = this.props;
        if (selectedLayerIndex !== null) {
            onUpdateLayer(selectedLayerIndex, { strokeColour: color.hex });
        }
    };

    handleStrokeWidthChange = (e) => {
        const { selectedLayerIndex, onUpdateLayer } = this.props;
        if (selectedLayerIndex !== null) {
            const value = e.target.value;
            // Allow empty string while typing
            if (value === '') {
                return;
            }
            const width = parseInt(value, 10);
            if (!isNaN(width) && width >= 0) {
                onUpdateLayer(selectedLayerIndex, { strokeWidth: width });
            }
        }
    };

    handleOpacityChange = (e) => {
        const { selectedLayerIndex, onUpdateLayer } = this.props;
        if (selectedLayerIndex !== null) {
            const opacity = parseFloat(e.target.value);
            if (!isNaN(opacity) && opacity >= 0 && opacity <= 1) {
                onUpdateLayer(selectedLayerIndex, { opacity });
            }
        }
    };

    handleRotationChange = (e) => {
        const { selectedLayerIndex, onUpdateLayer } = this.props;
        if (selectedLayerIndex !== null) {
            const rotation = parseInt(e.target.value, 10);
            if (!isNaN(rotation)) {
                onUpdateLayer(selectedLayerIndex, { rotation });
            }
        }
    };

    render() {
        const {
            selectedLayer,
            selectedLayerIndex,
            onAddLayer,
            onRemoveLayer,
            onZoomIn,
            onZoomOut,
            onSave,
            isSaved,
            isProcessing,
            processingStatus,
            supportedFonts,
            t = (key) => key, // Fallback if translation not provided
        } = this.props;

        const { showColorPicker, showStrokeColorPicker, editingText, textValue } = this.state;
        const isSelected = selectedLayerIndex !== null;

        return (
            <Col xs={12} id="rich-text-menu" className="rich-text-menu">
                <h4>{t('TemplateSnippet.menu_canvas')}</h4>

                {/* Zoom Controls */}
                <Row className="menu-row">
                    <Col xs={6}>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip">Zoom In</Tooltip>}
                        >
                            <button className="btn btn-dark btn-block" onClick={onZoomIn}>
                                <i className="fas fa-search-plus fa-lg" />
                            </button>
                        </OverlayTrigger>
                    </Col>
                    <Col xs={6}>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip">Zoom Out</Tooltip>}
                        >
                            <button className="btn btn-dark btn-block" onClick={onZoomOut}>
                                <i className="fas fa-search-minus fa-lg" />
                            </button>
                        </OverlayTrigger>
                    </Col>
                </Row>

                {/* Add/Remove Text Layer */}
                <Row className="menu-row">
                    <Col xs={6}>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip">Add Text Layer</Tooltip>}
                        >
                            <button className="btn btn-success btn-block" onClick={onAddLayer}>
                                <i className="fas fa-plus-circle fa-lg" />
                            </button>
                        </OverlayTrigger>
                    </Col>
                    <Col xs={6}>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip">Remove Text Layer</Tooltip>}
                        >
                            <button
                                className={`btn btn-${isSelected ? 'danger' : 'secondary'} btn-block`}
                                onClick={onRemoveLayer}
                                disabled={!isSelected}
                            >
                                <i className="fas fa-minus-circle fa-lg" />
                            </button>
                        </OverlayTrigger>
                    </Col>
                </Row>

                <hr />

                {/* Text Properties */}
                {isSelected && selectedLayer && (
                    <Fragment>
                        <h4>{t('TemplateSnippet.menu_text')}</h4>

                        {/* Editing Instructions */}
                        <div className="alert alert-info" style={{ padding: '8px', fontSize: '12px', marginBottom: '10px' }}>
                            <strong>How to edit:</strong>
                            <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                                <li>Click the text box on canvas to edit</li>
                                <li>Select text and use buttons below to format</li>
                                <li>Drag to reposition</li>
                            </ul>
                        </div>

                        {/* Font Family */}
                        <FormGroup>
                            <label>Font Family</label>
                            <FormControl
                                componentClass="select"
                                value={selectedLayer.fontFamily}
                                onChange={this.handleFontChange}
                            >
                                {supportedFonts.map((font) => (
                                    <option key={font} value={font}>
                                        {font}
                                    </option>
                                ))}
                            </FormControl>
                        </FormGroup>

                        {/* Font Size */}
                        <FormGroup>
                            <label>Font Size</label>
                            <div style={{ display: 'flex', alignItems: 'stretch' }}>
                                <button
                                    className="btn btn-default"
                                    onClick={() => {
                                        const newSize = Math.max(8, selectedLayer.fontSize - 2);
                                        this.props.onUpdateLayer(this.props.selectedLayerIndex, { fontSize: newSize });
                                    }}
                                    title="Decrease font size"
                                    style={{ flex: '0 0 auto', padding: '6px 12px' }}
                                >
                                    <i className="fas fa-minus" />
                                </button>
                                <FormControl
                                    type="number"
                                    value={selectedLayer.fontSize}
                                    onChange={this.handleFontSizeChange}
                                    min="8"
                                    max="300"
                                    style={{ textAlign: 'center', flex: '1 1 auto', borderLeft: 'none', borderRight: 'none' }}
                                />
                                <button
                                    className="btn btn-default"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const currentSize = parseInt(selectedLayer.fontSize, 10);
                                        const newSize = Math.min(300, currentSize + 2);
                                        this.props.onUpdateLayer(this.props.selectedLayerIndex, { fontSize: newSize });
                                    }}
                                    title="Increase font size"
                                    style={{ flex: '0 0 auto', padding: '6px 12px' }}
                                >
                                    <i className="fas fa-plus" />
                                </button>
                                <span className="input-group-addon" style={{ flex: '0 0 auto' }}>px</span>
                            </div>
                        </FormGroup>

                        {/* Text Formatting - Selection-based */}
                        <FormGroup>
                            <label>Text Formatting (Select text first)</label>
                            <Row>
                                <Col xs={4}>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="tooltip">Bold (Select text first)</Tooltip>}
                                    >
                                        <button
                                            className="btn btn-default btn-block"
                                            onClick={this.handleBold}
                                        >
                                            <i className="fas fa-bold" />
                                        </button>
                                    </OverlayTrigger>
                                </Col>
                                <Col xs={4}>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="tooltip">Italic (Select text first)</Tooltip>}
                                    >
                                        <button
                                            className="btn btn-default btn-block"
                                            onClick={this.handleItalic}
                                        >
                                            <i className="fas fa-italic" />
                                        </button>
                                    </OverlayTrigger>
                                </Col>
                                <Col xs={4}>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="tooltip">Underline (Select text first)</Tooltip>}
                                    >
                                        <button
                                            className="btn btn-default btn-block"
                                            onClick={this.handleUnderline}
                                        >
                                            <i className="fas fa-underline" />
                                        </button>
                                    </OverlayTrigger>
                                </Col>
                            </Row>
                            <small className="text-muted">
                                Click in the text box above, select text, then click these buttons to format
                            </small>
                        </FormGroup>

                        {/* Text Alignment */}
                        <FormGroup>
                            <label>Text Alignment</label>
                            <Row>
                                <Col xs={4}>
                                    <button
                                        className={`btn btn-block ${selectedLayer.textAlign === 'left' ? 'btn-primary active' : 'btn-default'}`}
                                        onClick={() => this.handleAlignChange('left')}
                                    >
                                        <i className="fas fa-align-left" />
                                    </button>
                                </Col>
                                <Col xs={4}>
                                    <button
                                        className={`btn btn-block ${selectedLayer.textAlign === 'center' ? 'btn-primary active' : 'btn-default'}`}
                                        onClick={() => this.handleAlignChange('center')}
                                    >
                                        <i className="fas fa-align-center" />
                                    </button>
                                </Col>
                                <Col xs={4}>
                                    <button
                                        className={`btn btn-block ${selectedLayer.textAlign === 'right' ? 'btn-primary active' : 'btn-default'}`}
                                        onClick={() => this.handleAlignChange('right')}
                                    >
                                        <i className="fas fa-align-right" />
                                    </button>
                                </Col>
                            </Row>
                        </FormGroup>

                        {/* Text Color */}
                        <FormGroup>
                            <label>Text Color (Select text to change specific words)</label>
                            <div
                                style={{
                                    padding: '5px',
                                    background: '#fff',
                                    borderRadius: '1px',
                                    boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                                    display: 'block',
                                    cursor: 'pointer',
                                    width: '100%',
                                }}
                                onMouseDown={(e) => {
                                    // Save selection BEFORE the click event changes focus
                                    if (!showColorPicker) {
                                        this.handleColorPickerOpen();
                                    }
                                }}
                                onClick={(e) => {
                                    // Handle close on click (after mousedown has already opened it)
                                    if (showColorPicker) {
                                        this.handleColorPickerClose();
                                    }
                                }}
                            >
                                <div
                                    style={{
                                        width: '100%',
                                        height: '30px',
                                        borderRadius: '2px',
                                        background: selectedLayer.textColour,
                                    }}
                                />
                            </div>
                            {showColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 9999 }}>
                                    <div
                                        style={{
                                            position: 'fixed',
                                            top: '0px',
                                            right: '0px',
                                            bottom: '0px',
                                            left: '0px',
                                        }}
                                        onClick={() => this.handleColorPickerClose()}
                                    />
                                    <SketchPicker
                                        color={this.state.tempColor || selectedLayer.textColour}
                                        onChange={this.handleColorChange}
                                    />
                                </div>
                            )}
                        </FormGroup>

                        {/* Stroke/Outline */}
                        <FormGroup>
                            <label>Stroke/Outline</label>
                            <div
                                style={{
                                    padding: '5px',
                                    background: '#fff',
                                    borderRadius: '1px',
                                    boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                                    display: 'block',
                                    cursor: 'pointer',
                                    marginBottom: '10px',
                                    width: '100%',
                                }}
                                onClick={() => this.setState({ showStrokeColorPicker: !showStrokeColorPicker })}
                            >
                                <div
                                    style={{
                                        width: '100%',
                                        height: '30px',
                                        borderRadius: '2px',
                                        background: selectedLayer.strokeColour || '#000000',
                                    }}
                                />
                            </div>
                            {showStrokeColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 9999 }}>
                                    <div
                                        style={{
                                            position: 'fixed',
                                            top: '0px',
                                            right: '0px',
                                            bottom: '0px',
                                            left: '0px',
                                        }}
                                        onClick={() => this.setState({ showStrokeColorPicker: false })}
                                    />
                                    <SketchPicker
                                        color={selectedLayer.strokeColour || '#000000'}
                                        onChange={this.handleStrokeColorChange}
                                    />
                                </div>
                            )}
                            <div className="input-group">
                                <span className="input-group-addon">Width</span>
                                <FormControl
                                    type="number"
                                    value={selectedLayer.strokeWidth}
                                    onChange={this.handleStrokeWidthChange}
                                    min="0"
                                    max="20"
                                />
                                <span className="input-group-addon">px</span>
                            </div>
                        </FormGroup>

                        {/* Opacity */}
                        <FormGroup>
                            <label>Opacity</label>
                            <FormControl
                                type="range"
                                value={selectedLayer.opacity}
                                onChange={this.handleOpacityChange}
                                min="0"
                                max="1"
                                step="0.1"
                            />
                            <small className="text-muted">{(selectedLayer.opacity * 100).toFixed(0)}%</small>
                        </FormGroup>

                        <hr />
                    </Fragment>
                )}

                {/* Save Button */}
                <FormGroup>
                    <button
                        className={`btn btn-block ${isSaved && !isProcessing ? 'btn-success' : 'btn-primary'}`}
                        onClick={onSave}
                        disabled={isProcessing}
                    >
                        {isProcessing ? (
                            <span>
                                <i className="fas fa-spinner fa-spin" /> Processing...
                            </span>
                        ) : isSaved ? (
                            <span>
                                <i className="fas fa-check" /> Saved
                            </span>
                        ) : (
                            <span>
                                <i className="fas fa-save" /> Save Changes
                            </span>
                        )}
                    </button>
                    {processingStatus && processingStatus !== 'none' && processingStatus !== 'complete' && (
                        <small className="text-muted">
                            Status: {processingStatus}
                        </small>
                    )}
                </FormGroup>

                {/* Custom footer content (e.g., view live version link) */}
                {this.props.customMenuFooter && (
                    <div style={{ marginTop: '10px' }}>
                        {this.props.customMenuFooter}
                    </div>
                )}
            </Col>
        );
    }
}

export default translate('translations')(RichTextMenu);
